import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Ban } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  green: { icon: CheckCircle2, color: 'text-emerald-600', dot: 'bg-emerald-500', label: 'OK' },
  yellow: { icon: AlertTriangle, color: 'text-amber-600', dot: 'bg-amber-500', label: 'Atención' },
  red: { icon: XCircle, color: 'text-red-600', dot: 'bg-red-500', label: 'Problema' },
  disabled: { icon: Ban, color: 'text-gray-400', dot: 'bg-gray-300', label: 'Deshabilitado' }
};

const formatLastSuccess = (iso) => {
  if (!iso) return 'Nunca corrió';
  const d = new Date(iso);
  return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const PersistenciaSyncSemaforo = ({ token }) => {
  const [syncHealth, setSyncHealth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await fetch(`${API_URL}/api/scheduler/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('status_error');
      const d = await response.json();
      setSyncHealth(d.sync_health || []);
    } catch (e) {
      console.error('Error cargando estado del scheduler:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const problemCount = syncHealth.filter((s) => s.status === 'red').length;
  const overallOk = !loading && !error && problemCount === 0;

  return (
    <Card data-testid="persistencia-sync-semaforo-card" className="border-l-4" style={{ borderLeftColor: error ? '#9ca3af' : overallOk ? '#10b981' : '#ef4444' }}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2" data-testid="sync-semaforo-header">
            {error ? (
              <span className="text-gray-500">No se pudo cargar el estado de los procesos</span>
            ) : loading ? (
              <span className="text-gray-500">Verificando estado de los procesos...</span>
            ) : overallOk ? (
              <span className="text-emerald-600">✅ Todo al día</span>
            ) : (
              <span className="text-red-600">🔴 {problemCount} proceso{problemCount !== 1 ? 's' : ''} con problema</span>
            )}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadStatus}
            disabled={loading}
            data-testid="sync-semaforo-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      {!loading && !error && (
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {syncHealth.map((s) => {
              const meta = STATUS_META[s.status] || STATUS_META.disabled;
              const Icon = meta.icon;
              return (
                <div
                  key={s.sync_type}
                  data-testid={`sync-semaforo-item-${s.sync_type}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-md border bg-gray-50"
                  title={s.status === 'disabled' ? 'Proceso deshabilitado en config' : `Último éxito: ${formatLastSuccess(s.last_success)}${s.hours_since != null ? ` (hace ${s.hours_since}h, umbral ${s.threshold_hours}h)` : ''}`}
                >
                  <Icon className={`h-4 w-4 shrink-0 ${meta.color}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{s.readable_name}</p>
                    <p className="text-xs text-gray-500">{formatLastSuccess(s.last_success)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default PersistenciaSyncSemaforo;
